import type {
  Citation,
  CitationGroup,
  CitationLocatorKind,
  ParsedNarrative,
} from "./types";

/** Must remain in lockstep with the agent and RMS citation-token helpers. */
export const CITATION_START = "\u{E200}";
export const CITATION_DELIMITER = "\u{E202}";
export const CITATION_STOP = "\u{E201}";

const LOCATOR_KINDS = new Set<CitationLocatorKind>([
  "line",
  "frame",
  "timestamp",
]);

interface CitationCluster {
  start: number;
  end: number;
  body: string;
  terminated: boolean;
}

/**
 * Find outermost sentinel-framed clusters with nesting awareness. Although a
 * nested START is never valid citation syntax, consuming the entire outer
 * frame prevents attacker-controlled prefix/suffix metadata from escaping
 * around an inner STOP. A truncated outer frame consumes the remaining text.
 */
function scanCitationClusters(text: string): CitationCluster[] {
  const clusters: CitationCluster[] = [];
  let cursor = 0;

  while (cursor < text.length) {
    const start = text.indexOf(CITATION_START, cursor);
    if (start === -1) break;

    let depth = 1;
    let scan = start + CITATION_START.length;
    let end = text.length;
    let terminated = false;

    while (scan < text.length) {
      if (text.startsWith(CITATION_START, scan)) {
        depth += 1;
        scan += CITATION_START.length;
        continue;
      }
      if (text.startsWith(CITATION_STOP, scan)) {
        depth -= 1;
        scan += CITATION_STOP.length;
        if (depth === 0) {
          end = scan;
          terminated = true;
          break;
        }
        continue;
      }
      scan += 1;
    }

    clusters.push({
      start,
      end,
      body: text.slice(
        start + CITATION_START.length,
        terminated ? end - CITATION_STOP.length : end,
      ),
      terminated,
    });
    cursor = end;
  }

  return clusters;
}

const ORPHAN_SENTINELS = new RegExp(
  `[${CITATION_START}${CITATION_DELIMITER}${CITATION_STOP}]`,
  "gu",
);

function stripOrphanSentinels(text: string): string {
  return text.replace(ORPHAN_SENTINELS, "");
}

/** Decode the contents of a sentinel-framed enriched citation token. */
export function decodeCitation(body: string): Citation | null {
  if (
    !body.startsWith(`cite${CITATION_DELIMITER}`) ||
    body.includes(CITATION_START) ||
    body.includes(CITATION_STOP)
  ) {
    return null;
  }

  const fields = body
    .slice("cite".length + CITATION_DELIMITER.length)
    .split(CITATION_DELIMITER);
  if (fields.length !== 7) return null;

  const [
    evidenceId,
    sourceLabel,
    startRaw,
    endRaw,
    kindRaw,
    locatorRaw,
    citedText,
  ] = fields;
  const startSeconds = Number(startRaw);
  const endSeconds = Number(endRaw);

  if (
    !evidenceId?.trim() ||
    !sourceLabel?.trim() ||
    !startRaw?.trim() ||
    !endRaw?.trim() ||
    !locatorRaw?.trim() ||
    !citedText.trim() ||
    !Number.isFinite(startSeconds) ||
    !Number.isFinite(endSeconds) ||
    startSeconds < 0 ||
    endSeconds < startSeconds ||
    !LOCATOR_KINDS.has(kindRaw as CitationLocatorKind)
  ) {
    return null;
  }

  return {
    evidenceId: evidenceId.trim(),
    sourceLabel: sourceLabel ?? "",
    startSeconds,
    endSeconds,
    locator: {
      kind: kindRaw as CitationLocatorKind,
      raw: locatorRaw ?? "",
    },
    citedText,
  };
}

/** Encode a decoded citation using the shared inline-token contract. */
export function encodeCitation(citation: Citation): string {
  return `${CITATION_START}${[
    "cite",
    citation.evidenceId,
    citation.sourceLabel,
    citation.startSeconds,
    citation.endSeconds,
    citation.locator.kind,
    citation.locator.raw,
    citation.citedText,
  ].join(CITATION_DELIMITER)}${CITATION_STOP}`;
}

/**
 * Replace each valid citation token while removing invalid/truncated clusters
 * and orphan sentinels. Useful when a renderer must extract private metadata
 * before passing the surrounding prose through another parser.
 */
export function replaceDecodedCitationTokens(
  text: string,
  replaceCitation: (citation: Citation, index: number) => string,
): string {
  let output = "";
  let cursor = 0;
  let citationIndex = 0;

  for (const cluster of scanCitationClusters(text)) {
    output += stripOrphanSentinels(text.slice(cursor, cluster.start));
    if (cluster.terminated) {
      const citation = decodeCitation(cluster.body);
      if (citation) output += replaceCitation(citation, citationIndex++);
    }
    cursor = cluster.end;
  }

  output += stripOrphanSentinels(text.slice(cursor));
  return output;
}

/**
 * Remove valid, invalid, and truncated citation markers from display text.
 * A truncated token consumes the remaining string because there is no reliable
 * boundary between its private payload and prose; hiding it is safer than
 * exposing evidence metadata.
 */
export function stripCitationTokens(text: string | null | undefined): string {
  if (!text) return text ?? "";
  if (
    !text.includes(CITATION_START) &&
    !text.includes(CITATION_DELIMITER) &&
    !text.includes(CITATION_STOP)
  ) {
    return text;
  }
  return replaceDecodedCitationTokens(text, () => "");
}

/**
 * Inverse of parseCitations: re-attach citation groups to clean prose at
 * their anchorEnd offsets. Emitting every citation of a group at the group's
 * anchorEnd is parse-stable \u2014 adjacent clusters re-merge into one group with
 * the same anchors on the next parseCitations pass.
 */
export function serializeCitations(
  cleanText: string,
  groups: CitationGroup[],
): string {
  if (groups.length === 0) return cleanText;
  const sorted = [...groups].sort((a, b) => a.anchorEnd - b.anchorEnd);
  let output = "";
  let cursor = 0;
  for (const group of sorted) {
    const at = Math.min(Math.max(group.anchorEnd, cursor), cleanText.length);
    output += cleanText.slice(cursor, at);
    for (const citation of group.citations) {
      output += encodeCitation(citation);
    }
    cursor = at;
  }
  output += cleanText.slice(cursor);
  return output;
}

const SENTENCE_BOUNDARY = /[.!?](?:["'\u2019\u201d)]*)\s|\n/g;

function anchorStartFor(
  cleanText: string,
  anchorEnd: number,
  floor: number,
): number {
  let start = floor;
  const slice = cleanText.slice(0, anchorEnd);
  SENTENCE_BOUNDARY.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = SENTENCE_BOUNDARY.exec(slice)) !== null) {
    const boundaryEnd = match.index + match[0].length;
    if (boundaryEnd > start && boundaryEnd < anchorEnd) start = boundaryEnd;
  }
  while (start < anchorEnd && /\s/.test(cleanText[start])) start += 1;
  return start;
}

/** Decode inline tokens and map each marker to its preceding sentence/clause. */
export function parseCitations(
  text: string | null | undefined,
): ParsedNarrative {
  if (!text) return { cleanText: text ?? "", groups: [] };
  if (!text.includes(CITATION_START)) {
    return { cleanText: stripOrphanSentinels(text), groups: [] };
  }

  let cleanText = "";
  let cursor = 0;
  let lastAnchorEnd = 0;
  const groups: CitationGroup[] = [];

  for (const cluster of scanCitationClusters(text)) {
    cleanText += stripOrphanSentinels(text.slice(cursor, cluster.start));
    const anchorEnd = cleanText.length;
    cursor = cluster.end;

    const citation = cluster.terminated
      ? decodeCitation(cluster.body)
      : null;
    if (!citation) continue;

    const previous = groups[groups.length - 1];
    // Tokens separated by nothing, or by whitespace alone, cite the same
    // claim: a whitespace-only gap would otherwise become its own group
    // whose anchor is empty after anchorStartFor skips the whitespace —
    // an invisible, zero-width citation.
    if (
      previous &&
      !cleanText.slice(previous.anchorEnd, anchorEnd).trim()
    ) {
      previous.citations.push(citation);
      continue;
    }

    const anchorStart = anchorStartFor(cleanText, anchorEnd, lastAnchorEnd);
    groups.push({ anchorStart, anchorEnd, citations: [citation] });
    lastAnchorEnd = anchorEnd;
  }

  cleanText += stripOrphanSentinels(text.slice(cursor));
  return { cleanText, groups };
}
